'use client';

import * as React from 'react';
import { Pattern } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CardRoot } from '@/components/ui/card';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import { Download, Copy, CheckCircle } from 'lucide-react';

interface PatternCardProps {
  pattern: Pattern;
  onClick?: () => void;
}

const tierColors = {
  S: { bg: '#9333ea', text: '#ffffff' },
  A: { bg: '#22c55e', text: '#ffffff' },
  B: { bg: '#3b82f6', text: '#ffffff' },
  C: { bg: '#6b7280', text: '#ffffff' },
} as const;

export function PatternCard({ pattern, onClick }: PatternCardProps) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const command = `vibeatlas install ${pattern.slug}`;

    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      toast.success('Install command copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy command');
    }
  };

  const tier = pattern.tier || 'C';
  const confidence = pattern.confidence ?? pattern.success_rate ?? 0;
  const tokensSaved = pattern.tokens_saved || 0;
  const successRate = pattern.success_rate || 0;

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <CardRoot
        hover="lift"
        className="p-6 cursor-pointer h-full flex flex-col focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2"
        onClick={onClick}
        role="article"
        aria-label={`Pattern: ${pattern.name}`}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.();
          }
        }}
      >
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-semibold text-gray-900">{pattern.name}</h3>
            <Badge
              size="sm"
              customColor={tierColors[tier]}
              className="font-bold"
              aria-label={`Tier ${tier}`}
            >
              {tier}
            </Badge>
          </div>
          <span className="text-sm text-gray-500" aria-label={`Version ${pattern.version}`}>v{pattern.version}</span>
        </div>

        <p className="text-gray-600 mb-4 line-clamp-2 flex-grow">
          {pattern.description}
        </p>

        <dl className="space-y-3 mb-4">
          <div className="flex items-center justify-between text-sm">
            <dt className="text-gray-600">Confidence</dt>
            <dd className="text-green-600 font-semibold">{confidence}%</dd>
          </div>

          <div className="flex items-center justify-between text-sm">
            <dt className="text-gray-600">Tokens Saved</dt>
            <dd className="font-semibold">{tokensSaved.toLocaleString()}</dd>
          </div>

          <div className="flex items-center justify-between text-sm">
            <dt className="flex items-center gap-1">
              <CheckCircle className="w-4 h-4 text-green-600" aria-hidden="true" />
              <span className="text-gray-600">Success Rate</span>
            </dt>
            <dd className="font-semibold">{successRate}%</dd>
          </div>
        </dl>

        <div className="flex flex-wrap gap-2 mb-4">
          {pattern.tags.slice(0, 3).map((tag) => (
            <Badge key={tag} variant="info" size="sm">
              {tag}
            </Badge>
          ))}
          {pattern.tags.length > 3 && (
            <Badge variant="default" size="sm">
              +{pattern.tags.length - 3}
            </Badge>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-auto">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 text-gray-600" aria-label={`${pattern.installs?.toLocaleString() || 0} installs`}>
              <Download className="w-4 h-4" aria-hidden="true" />
              <span className="text-sm font-medium">
                {pattern.installs?.toLocaleString() || 0}
              </span>
            </div>
            <span className="text-xs text-gray-500">
              {formatDistanceToNow(new Date(pattern.created_at), { addSuffix: true })}
            </span>
          </div>

          <Button
            size="sm"
            variant="ghost"
            onClick={handleCopy}
            iconLeft={copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            aria-label={copied ? 'Install command copied' : 'Copy install command'}
          >
            {copied ? 'Copied!' : 'Quick Install'}
          </Button>
        </div>
      </CardRoot>
    </motion.div>
  );
}
